import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const resourceTypes = [
  'article',
  'video',
  'audio',
  'illustration',
  'infographic',
  'interactive_diagram',
  'decision_guide',
  'downloadable_pdf',
  'checklist',
  'faq',
  'scenario',
  'ai_supported_explanation'
];

const mediaTypes = ['video', 'audio', 'image', 'document', 'embed', 'interactive', 'thumbnail', 'transcript', 'caption'];
const audiences = ['client', 'advisor', 'attorney', 'professional', 'all'];
const statuses = ['draft', 'published', 'archived'];

const emptyTopicForm = {
  title: '',
  slug: '',
  content_category: 'Estate Planning Basics',
  audience: 'client',
  short_summary: '',
  why_this_matters: '',
  why_we_are_asking: '',
  plain_english_explanation: '',
  full_explanation: '',
  common_questions: '',
  common_misconceptions: '',
  common_mistakes: '',
  examples: '',
  advisor_discussion_questions: '',
  attorney_discussion_questions: '',
  display_priority: 100,
  publication_status: 'draft'
};

const emptyResourceForm = {
  title: '',
  slug: '',
  resource_type: 'article',
  short_description: '',
  content_category: 'Estate Planning Basics',
  audience: 'client',
  content_markdown: '',
  media_url: '',
  video_url: '',
  thumbnail_url: '',
  estimated_minutes: 5,
  publication_status: 'draft',
  is_active: true
};

const emptyMediaForm = {
  media_type: 'video',
  source_type: 'external_url',
  title: '',
  description: '',
  media_url: '',
  embed_url: '',
  thumbnail_url: '',
  duration_seconds: '',
  alt_text: '',
  transcript_text: '',
  caption_url: '',
  is_primary: false,
  is_downloadable: false,
  display_priority: 100,
  publication_status: 'published'
};

const slugify = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const linesToArray = (value) => String(value || '')
  .split('\n')
  .map((line) => line.trim())
  .filter(Boolean);

const arrayToLines = (value) => Array.isArray(value) ? value.join('\n') : '';

const qaLinesToJson = (value) => linesToArray(value).map((line) => {
  const [question, ...answerParts] = line.split('|');
  return {
    question: question?.trim() || '',
    answer: answerParts.join('|').trim()
  };
}).filter((item) => item.question || item.answer);

const qaJsonToLines = (value) => Array.isArray(value)
  ? value.map((item) => `${item.question || ''} | ${item.answer || ''}`.trim()).join('\n')
  : '';

const toTopicForm = (topic) => ({
  title: topic.title || '',
  slug: topic.slug || '',
  content_category: topic.content_category || 'Estate Planning Basics',
  audience: topic.audience || 'client',
  short_summary: topic.short_summary || '',
  why_this_matters: topic.why_this_matters || '',
  why_we_are_asking: topic.why_we_are_asking || '',
  plain_english_explanation: topic.plain_english_explanation || '',
  full_explanation: topic.full_explanation || '',
  common_questions: qaJsonToLines(topic.common_questions_json),
  common_misconceptions: arrayToLines(topic.common_misconceptions_json),
  common_mistakes: arrayToLines(topic.common_mistakes_json),
  examples: arrayToLines(topic.examples_json),
  advisor_discussion_questions: arrayToLines(topic.advisor_discussion_questions_json),
  attorney_discussion_questions: arrayToLines(topic.attorney_discussion_questions_json),
  display_priority: topic.display_priority || 100,
  publication_status: topic.publication_status || 'draft'
});

const toResourceForm = (resource) => ({
  title: resource.title || '',
  slug: resource.slug || '',
  resource_type: resource.resource_type || 'article',
  short_description: resource.short_description || '',
  content_category: resource.content_category || 'Estate Planning Basics',
  audience: resource.audience || 'client',
  content_markdown: resource.content_markdown || '',
  media_url: resource.media_url || '',
  video_url: resource.video_url || '',
  thumbnail_url: resource.thumbnail_url || '',
  estimated_minutes: resource.estimated_minutes || 5,
  publication_status: resource.publication_status || 'draft',
  is_active: resource.is_active !== false
});

const LearningContentAdminPage = () => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState('topics');
  const [topics, setTopics] = useState([]);
  const [resources, setResources] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState('');
  const [selectedResourceId, setSelectedResourceId] = useState('');
  const [topicForm, setTopicForm] = useState(emptyTopicForm);
  const [resourceForm, setResourceForm] = useState(emptyResourceForm);
  const [topicResourceLinks, setTopicResourceLinks] = useState([]);
  const [topicResourceId, setTopicResourceId] = useState('');
  const [topicResourceRelationship, setTopicResourceRelationship] = useState('learn_more');
  const [mediaItems, setMediaItems] = useState([]);
  const [mediaForm, setMediaForm] = useState(emptyMediaForm);
  const [topicSearch, setTopicSearch] = useState('');
  const [resourceSearch, setResourceSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mediaSaving, setMediaSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const selectedTopic = useMemo(
    () => topics.find((topic) => topic.id === selectedTopicId),
    [topics, selectedTopicId]
  );

  const selectedResource = useMemo(
    () => resources.find((resource) => resource.id === selectedResourceId),
    [resources, selectedResourceId]
  );

  const filteredTopics = useMemo(() => {
    const query = topicSearch.trim().toLowerCase();
    if (!query) return topics;
    return topics.filter((topic) => [
      topic.title,
      topic.slug,
      topic.content_category,
      topic.short_summary
    ].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [topics, topicSearch]);

  const filteredResources = useMemo(() => {
    const query = resourceSearch.trim().toLowerCase();
    if (!query) return resources;
    return resources.filter((resource) => [
      resource.title,
      resource.slug,
      resource.resource_type,
      resource.content_category,
      resource.short_description
    ].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [resources, resourceSearch]);

  const loadCatalog = async ({ preferredTopicId = selectedTopicId, preferredResourceId = selectedResourceId } = {}) => {
    const [{ data: topicRows, error: topicError }, { data: resourceRows, error: resourceError }] = await Promise.all([
      supabase
        .from('education_topics')
        .select('*')
        .order('display_priority', { ascending: true })
        .order('title', { ascending: true }),
      supabase
        .from('education_resources')
        .select('*')
        .order('title', { ascending: true })
    ]);

    if (topicError) throw topicError;
    if (resourceError) throw resourceError;

    const nextTopics = topicRows || [];
    const nextResources = resourceRows || [];
    setTopics(nextTopics);
    setResources(nextResources);

    const nextTopicId = preferredTopicId && nextTopics.some((topic) => topic.id === preferredTopicId)
      ? preferredTopicId
      : nextTopics[0]?.id || '';
    const nextResourceId = preferredResourceId && nextResources.some((resource) => resource.id === preferredResourceId)
      ? preferredResourceId
      : nextResources[0]?.id || '';

    setSelectedTopicId(nextTopicId);
    setSelectedResourceId(nextResourceId);
  };

  const loadMediaItems = async (resourceId) => {
    if (!resourceId) {
      setMediaItems([]);
      return;
    }

    const { data, error: mediaError } = await supabase
      .from('education_resource_media')
      .select('*')
      .eq('resource_id', resourceId)
      .order('display_priority', { ascending: true })
      .order('created_at', { ascending: true });

    if (mediaError) throw mediaError;
    setMediaItems(data || []);
  };

  const loadTopicResourceLinks = async (topicId) => {
    if (!topicId) {
      setTopicResourceLinks([]);
      return;
    }

    const { data, error: linksError } = await supabase
      .from('education_topic_resources')
      .select('id, resource_id, relationship_type, display_priority, is_active, resource:education_resources(id, title, slug, resource_type, short_description)')
      .eq('topic_id', topicId)
      .order('display_priority', { ascending: true })
      .order('created_at', { ascending: true });

    if (linksError) throw linksError;
    setTopicResourceLinks(data || []);
  };

  useEffect(() => {
    const loadPage = async () => {
      if (profile?.role !== 'platform_admin') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        await loadCatalog();
      } catch (err) {
        console.error('Learning content admin load error:', err);
        setError(err.message || 'Unable to load learning content.');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [profile?.role]);

  useEffect(() => {
    setTopicForm(selectedTopic ? toTopicForm(selectedTopic) : emptyTopicForm);
    setTopicResourceId('');
    loadTopicResourceLinks(selectedTopic?.id).catch((err) => {
      console.error('Topic resource link load error:', err);
      setError(err.message || 'Unable to load topic resource links.');
    });
  }, [selectedTopic?.id]);

  useEffect(() => {
    setResourceForm(selectedResource ? toResourceForm(selectedResource) : emptyResourceForm);
    setMediaForm(emptyMediaForm);
    loadMediaItems(selectedResource?.id).catch((err) => {
      console.error('Media load error:', err);
      setError(err.message || 'Unable to load media assets.');
    });
  }, [selectedResource?.id]);

  const updateTopicForm = (key, value) => {
    setTopicForm((current) => ({
      ...current,
      [key]: value,
      ...(key === 'title' && !selectedTopicId ? { slug: slugify(value) } : {})
    }));
  };

  const updateResourceForm = (key, value) => {
    setResourceForm((current) => ({
      ...current,
      [key]: value,
      ...(key === 'title' && !selectedResourceId ? { slug: slugify(value) } : {})
    }));
  };

  const startNewTopic = () => {
    setSelectedTopicId('');
    setTopicForm(emptyTopicForm);
    setActiveTab('topics');
    setNotice(null);
    setError(null);
  };

  const startNewResource = () => {
    setSelectedResourceId('');
    setResourceForm(emptyResourceForm);
    setMediaItems([]);
    setMediaForm(emptyMediaForm);
    setActiveTab('resources');
    setNotice(null);
    setError(null);
  };

  const saveTopic = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setNotice(null);

      if (!topicForm.title.trim()) throw new Error('Topic title is required.');
      if (!topicForm.slug.trim()) throw new Error('Topic slug is required.');

      const payload = {
        title: topicForm.title.trim(),
        slug: slugify(topicForm.slug),
        content_category: topicForm.content_category.trim() || 'Estate Planning Basics',
        audience: topicForm.audience,
        short_summary: topicForm.short_summary.trim() || null,
        why_this_matters: topicForm.why_this_matters.trim() || null,
        why_we_are_asking: topicForm.why_we_are_asking.trim() || null,
        plain_english_explanation: topicForm.plain_english_explanation.trim() || null,
        full_explanation: topicForm.full_explanation.trim() || null,
        common_questions_json: qaLinesToJson(topicForm.common_questions),
        common_misconceptions_json: linesToArray(topicForm.common_misconceptions),
        common_mistakes_json: linesToArray(topicForm.common_mistakes),
        examples_json: linesToArray(topicForm.examples),
        advisor_discussion_questions_json: linesToArray(topicForm.advisor_discussion_questions),
        attorney_discussion_questions_json: linesToArray(topicForm.attorney_discussion_questions),
        display_priority: Number(topicForm.display_priority) || 100,
        publication_status: topicForm.publication_status,
        updated_at: new Date().toISOString()
      };

      let saved;
      if (selectedTopicId) {
        const { data, error: updateError } = await supabase
          .from('education_topics')
          .update(payload)
          .eq('id', selectedTopicId)
          .select('*')
          .single();
        if (updateError) throw updateError;
        saved = data;
      } else {
        const { data, error: insertError } = await supabase
          .from('education_topics')
          .insert(payload)
          .select('*')
          .single();
        if (insertError) throw insertError;
        saved = data;
      }

      await loadCatalog({ preferredTopicId: saved.id, preferredResourceId: selectedResourceId });
      setNotice('Learning topic saved.');
    } catch (err) {
      console.error('Topic save error:', err);
      setError(err.message || 'Unable to save learning topic.');
    } finally {
      setSaving(false);
    }
  };

  const saveResource = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setNotice(null);

      if (!resourceForm.title.trim()) throw new Error('Resource title is required.');
      if (!resourceForm.slug.trim()) throw new Error('Resource slug is required.');

      const payload = {
        title: resourceForm.title.trim(),
        slug: slugify(resourceForm.slug),
        resource_type: resourceForm.resource_type,
        short_description: resourceForm.short_description.trim() || null,
        content_category: resourceForm.content_category.trim() || 'Estate Planning Basics',
        audience: resourceForm.audience,
        content_markdown: resourceForm.content_markdown.trim() || null,
        media_url: resourceForm.media_url.trim() || null,
        video_url: resourceForm.video_url.trim() || null,
        thumbnail_url: resourceForm.thumbnail_url.trim() || null,
        estimated_minutes: Number(resourceForm.estimated_minutes) || 5,
        publication_status: resourceForm.publication_status,
        is_active: resourceForm.is_active,
        updated_at: new Date().toISOString()
      };

      let saved;
      if (selectedResourceId) {
        const { data, error: updateError } = await supabase
          .from('education_resources')
          .update(payload)
          .eq('id', selectedResourceId)
          .select('*')
          .single();
        if (updateError) throw updateError;
        saved = data;
      } else {
        const { data, error: insertError } = await supabase
          .from('education_resources')
          .insert(payload)
          .select('*')
          .single();
        if (insertError) throw insertError;
        saved = data;
      }

      await loadCatalog({ preferredTopicId: selectedTopicId, preferredResourceId: saved.id });
      setNotice('Learning resource saved.');
    } catch (err) {
      console.error('Resource save error:', err);
      setError(err.message || 'Unable to save learning resource.');
    } finally {
      setSaving(false);
    }
  };

  const addTopicResourceLink = async () => {
    if (!selectedTopicId || !topicResourceId) return;

    try {
      setMediaSaving(true);
      setError(null);
      setNotice(null);

      const nextPriority = (topicResourceLinks.at(-1)?.display_priority || 0) + 10;
      const { error: insertError } = await supabase
        .from('education_topic_resources')
        .insert({
          topic_id: selectedTopicId,
          resource_id: topicResourceId,
          relationship_type: topicResourceRelationship,
          display_priority: nextPriority,
          is_active: true
        });

      if (insertError) throw insertError;

      setTopicResourceId('');
      await loadTopicResourceLinks(selectedTopicId);
      setNotice('Resource linked to topic.');
    } catch (err) {
      console.error('Topic resource link save error:', err);
      setError(err.message || 'Unable to link resource to topic.');
    } finally {
      setMediaSaving(false);
    }
  };

  const removeTopicResourceLink = async (link) => {
    try {
      setMediaSaving(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('education_topic_resources')
        .delete()
        .eq('id', link.id);

      if (deleteError) throw deleteError;

      await loadTopicResourceLinks(selectedTopicId);
      setNotice('Resource removed from topic.');
    } catch (err) {
      console.error('Topic resource link delete error:', err);
      setError(err.message || 'Unable to remove resource from topic.');
    } finally {
      setMediaSaving(false);
    }
  };

  const saveMedia = async (event) => {
    event.preventDefault();
    if (!selectedResourceId) return;

    try {
      setMediaSaving(true);
      setError(null);
      setNotice(null);

      if (!mediaForm.title.trim()) throw new Error('Media title is required.');

      const payload = {
        resource_id: selectedResourceId,
        media_type: mediaForm.media_type,
        source_type: mediaForm.source_type,
        title: mediaForm.title.trim(),
        description: mediaForm.description.trim() || null,
        media_url: mediaForm.media_url.trim() || null,
        embed_url: mediaForm.embed_url.trim() || null,
        thumbnail_url: mediaForm.thumbnail_url.trim() || null,
        duration_seconds: mediaForm.duration_seconds ? Number(mediaForm.duration_seconds) : null,
        alt_text: mediaForm.alt_text.trim() || null,
        transcript_text: mediaForm.transcript_text.trim() || null,
        caption_url: mediaForm.caption_url.trim() || null,
        is_primary: mediaForm.is_primary,
        is_downloadable: mediaForm.is_downloadable,
        display_priority: Number(mediaForm.display_priority) || 100,
        publication_status: mediaForm.publication_status
      };

      const { error: insertError } = await supabase
        .from('education_resource_media')
        .insert(payload);

      if (insertError) throw insertError;

      setMediaForm(emptyMediaForm);
      await loadMediaItems(selectedResourceId);
      setNotice('Media asset added.');
    } catch (err) {
      console.error('Media save error:', err);
      setError(err.message || 'Unable to add media asset.');
    } finally {
      setMediaSaving(false);
    }
  };

  const removeMedia = async (item) => {
    try {
      setMediaSaving(true);
      setError(null);
      const { error: deleteError } = await supabase
        .from('education_resource_media')
        .delete()
        .eq('id', item.id);

      if (deleteError) throw deleteError;
      await loadMediaItems(selectedResourceId);
      setNotice('Media asset removed.');
    } catch (err) {
      console.error('Media delete error:', err);
      setError(err.message || 'Unable to remove media asset.');
    } finally {
      setMediaSaving(false);
    }
  };

  if (profile?.role !== 'platform_admin') {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
        <SafeIcon icon={FiIcons.FiLock} className="mx-auto mb-4 h-10 w-10 text-slate-300" />
        <h1 className="text-lg font-bold text-slate-900">Platform admin access required</h1>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <SafeIcon icon={FiIcons.FiLoader} className="h-10 w-10 animate-spin text-slate-300" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-12">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Learning Content</h1>
          <p className="mt-1 text-sm text-slate-500">
            Create and maintain the canonical education topics and resources used throughout the client learning system.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={startNewTopic}
            className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <SafeIcon icon={FiIcons.FiPlus} className="mr-2 h-4 w-4" />
            New Topic
          </button>
          <button
            type="button"
            onClick={startNewResource}
            className="inline-flex items-center justify-center rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900"
          >
            <SafeIcon icon={FiIcons.FiPlus} className="mr-2 h-4 w-4" />
            New Resource
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border-l-4 border-red-400 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {notice && (
        <div className="rounded-md border-l-4 border-green-400 bg-green-50 p-4 text-sm text-green-700">
          {notice}
        </div>
      )}

      <div className="flex gap-2 rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
        {[
          { key: 'topics', label: 'Topics', icon: FiIcons.FiBookOpen },
          { key: 'resources', label: 'Resources', icon: FiIcons.FiFileText }
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`inline-flex flex-1 items-center justify-center rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              activeTab === tab.key ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
            }`}
          >
            <SafeIcon icon={tab.icon} className="mr-2 h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'topics' ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[340px_1fr]">
          <aside className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 p-4">
              <label className="relative block">
                <SafeIcon icon={FiIcons.FiSearch} className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  type="search"
                  value={topicSearch}
                  onChange={(event) => setTopicSearch(event.target.value)}
                  placeholder="Search topics"
                  className="block w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-slate-500"
                />
              </label>
            </div>
            <div className="max-h-[720px] divide-y divide-slate-100 overflow-y-auto">
              {filteredTopics.map((topic) => (
                <button
                  key={topic.id}
                  type="button"
                  onClick={() => setSelectedTopicId(topic.id)}
                  className={`block w-full px-5 py-4 text-left transition-colors ${
                    selectedTopicId === topic.id ? 'bg-blue-50/70' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold text-slate-900">{topic.title}</p>
                      <p className="mt-1 text-xs text-slate-500">{topic.content_category}</p>
                    </div>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                      topic.publication_status === 'published'
                        ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 bg-slate-100 text-slate-500'
                    }`}>
                      {topic.publication_status}
                    </span>
                  </div>
                </button>
              ))}
              {filteredTopics.length === 0 && (
                <div className="p-6 text-center text-sm text-slate-500">No topics found.</div>
              )}
            </div>
          </aside>

          <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-6 py-4">
              <h2 className="text-sm font-bold uppercase tracking-tight text-slate-800">
                {selectedTopicId ? 'Edit Topic' : 'Create Topic'}
              </h2>
            </div>
            <form onSubmit={saveTopic} className="space-y-5 p-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="text-sm font-medium text-slate-700">
                  Title
                  <input
                    type="text"
                    value={topicForm.title}
                    onChange={(event) => updateTopicForm('title', event.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                    required
                  />
                </label>
                <label className="text-sm font-medium text-slate-700">
                  Slug
                  <input
                    type="text"
                    value={topicForm.slug}
                    onChange={(event) => updateTopicForm('slug', event.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                    required
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_150px_150px_130px]">
                <label className="text-sm font-medium text-slate-700">
                  Category
                  <input
                    type="text"
                    value={topicForm.content_category}
                    onChange={(event) => updateTopicForm('content_category', event.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                </label>
                <label className="text-sm font-medium text-slate-700">
                  Audience
                  <select
                    value={topicForm.audience}
                    onChange={(event) => updateTopicForm('audience', event.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500"
                  >
                    {audiences.map((audience) => <option key={audience} value={audience}>{audience}</option>)}
                  </select>
                </label>
                <label className="text-sm font-medium text-slate-700">
                  Status
                  <select
                    value={topicForm.publication_status}
                    onChange={(event) => updateTopicForm('publication_status', event.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500"
                  >
                    {statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                  </select>
                </label>
                <label className="text-sm font-medium text-slate-700">
                  Priority
                  <input
                    type="number"
                    value={topicForm.display_priority}
                    onChange={(event) => updateTopicForm('display_priority', event.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                </label>
              </div>

              {[
                ['short_summary', 'Short Summary', 3],
                ['why_this_matters', 'Why This Matters', 3],
                ['why_we_are_asking', "Why We're Asking", 3],
                ['plain_english_explanation', 'Plain English Explanation', 4],
                ['full_explanation', 'Full Explanation', 6]
              ].map(([key, label, rows]) => (
                <label key={key} className="block text-sm font-medium text-slate-700">
                  {label}
                  <textarea
                    rows={rows}
                    value={topicForm[key]}
                    onChange={(event) => updateTopicForm(key, event.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                </label>
              ))}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="block text-sm font-medium text-slate-700">
                  Common Questions
                  <textarea
                    rows={5}
                    value={topicForm.common_questions}
                    onChange={(event) => updateTopicForm('common_questions', event.target.value)}
                    placeholder="Question | Answer"
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Common Misconceptions
                  <textarea
                    rows={5}
                    value={topicForm.common_misconceptions}
                    onChange={(event) => updateTopicForm('common_misconceptions', event.target.value)}
                    placeholder="One misconception per line"
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Examples
                  <textarea
                    rows={4}
                    value={topicForm.examples}
                    onChange={(event) => updateTopicForm('examples', event.target.value)}
                    placeholder="One example per line"
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Common Mistakes
                  <textarea
                    rows={4}
                    value={topicForm.common_mistakes}
                    onChange={(event) => updateTopicForm('common_mistakes', event.target.value)}
                    placeholder="One mistake per line"
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Advisor Discussion Questions
                  <textarea
                    rows={4}
                    value={topicForm.advisor_discussion_questions}
                    onChange={(event) => updateTopicForm('advisor_discussion_questions', event.target.value)}
                    placeholder="One question per line"
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Attorney Discussion Questions
                  <textarea
                    rows={4}
                    value={topicForm.attorney_discussion_questions}
                    onChange={(event) => updateTopicForm('attorney_discussion_questions', event.target.value)}
                    placeholder="One question per line"
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                </label>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center justify-center rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50"
                >
                  <SafeIcon icon={saving ? FiIcons.FiLoader : FiIcons.FiSave} className={`mr-2 h-4 w-4 ${saving ? 'animate-spin' : ''}`} />
                  Save Topic
                </button>
              </div>
            </form>

            <div className="border-t border-slate-100 p-6">
              <div className="mb-4">
                <h3 className="text-sm font-bold uppercase tracking-tight text-slate-800">Related Resources</h3>
                <p className="mt-1 text-xs text-slate-500">Choose which resources appear on this topic page.</p>
              </div>

              {!selectedTopicId ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                  Save a topic before linking resources.
                </div>
              ) : (
                <div className="space-y-4">
                  {topicResourceLinks.length > 0 ? (
                    <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                      {topicResourceLinks.map((link) => (
                        <div key={link.id} className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
                          <div>
                            <div className="mb-1 flex flex-wrap items-center gap-2">
                              <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                {link.relationship_type}
                              </span>
                              <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                                link.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                              }`}>
                                {link.is_active ? 'Active' : 'Hidden'}
                              </span>
                            </div>
                            <p className="font-semibold text-slate-900">{link.resource?.title || 'Learning Resource'}</p>
                            <p className="mt-1 text-xs text-slate-500">{link.resource?.short_description || link.resource?.resource_type}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeTopicResourceLink(link)}
                            disabled={mediaSaving}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-red-100 bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-40"
                            title="Remove resource"
                          >
                            <SafeIcon icon={FiIcons.FiTrash2} className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                      No resources linked to this topic yet.
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 lg:grid-cols-[1fr_160px_auto] lg:items-end">
                    <label className="text-sm font-medium text-slate-700">
                      Resource
                      <select
                        value={topicResourceId}
                        onChange={(event) => setTopicResourceId(event.target.value)}
                        className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500"
                      >
                        <option value="">Choose resource</option>
                        {resources.map((resource) => (
                          <option key={resource.id} value={resource.id}>{resource.title}</option>
                        ))}
                      </select>
                    </label>
                    <label className="text-sm font-medium text-slate-700">
                      Relationship
                      <select
                        value={topicResourceRelationship}
                        onChange={(event) => setTopicResourceRelationship(event.target.value)}
                        className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500"
                      >
                        <option value="primary">primary</option>
                        <option value="learn_more">learn_more</option>
                        <option value="related">related</option>
                        <option value="example">example</option>
                        <option value="media">media</option>
                        <option value="download">download</option>
                      </select>
                    </label>
                    <button
                      type="button"
                      onClick={addTopicResourceLink}
                      disabled={!topicResourceId || mediaSaving}
                      className="inline-flex items-center justify-center rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50"
                    >
                      <SafeIcon icon={mediaSaving ? FiIcons.FiLoader : FiIcons.FiPlus} className={`mr-2 h-4 w-4 ${mediaSaving ? 'animate-spin' : ''}`} />
                      Link
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[340px_1fr]">
          <aside className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 p-4">
              <label className="relative block">
                <SafeIcon icon={FiIcons.FiSearch} className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  type="search"
                  value={resourceSearch}
                  onChange={(event) => setResourceSearch(event.target.value)}
                  placeholder="Search resources"
                  className="block w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-slate-500"
                />
              </label>
            </div>
            <div className="max-h-[720px] divide-y divide-slate-100 overflow-y-auto">
              {filteredResources.map((resource) => (
                <button
                  key={resource.id}
                  type="button"
                  onClick={() => setSelectedResourceId(resource.id)}
                  className={`block w-full px-5 py-4 text-left transition-colors ${
                    selectedResourceId === resource.id ? 'bg-blue-50/70' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold text-slate-900">{resource.title}</p>
                      <p className="mt-1 text-xs text-slate-500">{resource.resource_type}</p>
                    </div>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                      resource.is_active && resource.publication_status === 'published'
                        ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 bg-slate-100 text-slate-500'
                    }`}>
                      {resource.is_active ? resource.publication_status : 'hidden'}
                    </span>
                  </div>
                </button>
              ))}
              {filteredResources.length === 0 && (
                <div className="p-6 text-center text-sm text-slate-500">No resources found.</div>
              )}
            </div>
          </aside>

          <div className="space-y-6">
            <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-6 py-4">
                <h2 className="text-sm font-bold uppercase tracking-tight text-slate-800">
                  {selectedResourceId ? 'Edit Resource' : 'Create Resource'}
                </h2>
              </div>
              <form onSubmit={saveResource} className="space-y-5 p-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <label className="text-sm font-medium text-slate-700">
                    Title
                    <input
                      type="text"
                      value={resourceForm.title}
                      onChange={(event) => updateResourceForm('title', event.target.value)}
                      className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                      required
                    />
                  </label>
                  <label className="text-sm font-medium text-slate-700">
                    Slug
                    <input
                      type="text"
                      value={resourceForm.slug}
                      onChange={(event) => updateResourceForm('slug', event.target.value)}
                      className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                      required
                    />
                  </label>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_170px_140px_130px]">
                  <label className="text-sm font-medium text-slate-700">
                    Category
                    <input
                      type="text"
                      value={resourceForm.content_category}
                      onChange={(event) => updateResourceForm('content_category', event.target.value)}
                      className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                    />
                  </label>
                  <label className="text-sm font-medium text-slate-700">
                    Type
                    <select
                      value={resourceForm.resource_type}
                      onChange={(event) => updateResourceForm('resource_type', event.target.value)}
                      className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500"
                    >
                      {resourceTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                    </select>
                  </label>
                  <label className="text-sm font-medium text-slate-700">
                    Status
                    <select
                      value={resourceForm.publication_status}
                      onChange={(event) => updateResourceForm('publication_status', event.target.value)}
                      className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500"
                    >
                      {statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                    </select>
                  </label>
                  <label className="text-sm font-medium text-slate-700">
                    Minutes
                    <input
                      type="number"
                      value={resourceForm.estimated_minutes}
                      onChange={(event) => updateResourceForm('estimated_minutes', event.target.value)}
                      className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                    />
                  </label>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-[170px_1fr]">
                  <label className="text-sm font-medium text-slate-700">
                    Audience
                    <select
                      value={resourceForm.audience}
                      onChange={(event) => updateResourceForm('audience', event.target.value)}
                      className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500"
                    >
                      {audiences.map((audience) => <option key={audience} value={audience}>{audience}</option>)}
                    </select>
                  </label>
                  <label className="flex items-end gap-2 pb-2 text-sm font-medium text-slate-700">
                    <input
                      type="checkbox"
                      checked={resourceForm.is_active}
                      onChange={(event) => updateResourceForm('is_active', event.target.checked)}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    Active
                  </label>
                </div>

                <label className="block text-sm font-medium text-slate-700">
                  Short Description
                  <textarea
                    rows={3}
                    value={resourceForm.short_description}
                    onChange={(event) => updateResourceForm('short_description', event.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                </label>

                <label className="block text-sm font-medium text-slate-700">
                  Content
                  <textarea
                    rows={10}
                    value={resourceForm.content_markdown}
                    onChange={(event) => updateResourceForm('content_markdown', event.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                </label>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <label className="text-sm font-medium text-slate-700">
                    Media URL
                    <input
                      type="url"
                      value={resourceForm.media_url}
                      onChange={(event) => updateResourceForm('media_url', event.target.value)}
                      className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                    />
                  </label>
                  <label className="text-sm font-medium text-slate-700">
                    Legacy Video URL
                    <input
                      type="url"
                      value={resourceForm.video_url}
                      onChange={(event) => updateResourceForm('video_url', event.target.value)}
                      className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                    />
                  </label>
                  <label className="text-sm font-medium text-slate-700">
                    Thumbnail URL
                    <input
                      type="url"
                      value={resourceForm.thumbnail_url}
                      onChange={(event) => updateResourceForm('thumbnail_url', event.target.value)}
                      className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                    />
                  </label>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center justify-center rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50"
                  >
                    <SafeIcon icon={saving ? FiIcons.FiLoader : FiIcons.FiSave} className={`mr-2 h-4 w-4 ${saving ? 'animate-spin' : ''}`} />
                    Save Resource
                  </button>
                </div>
              </form>
            </section>

            <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-6 py-4">
                <h2 className="text-sm font-bold uppercase tracking-tight text-slate-800">Media Assets</h2>
                <p className="mt-1 text-xs text-slate-500">Attach externally hosted media, embeds, transcripts, captions, thumbnails, or downloadable files.</p>
              </div>
              <div className="space-y-5 p-6">
                {!selectedResourceId ? (
                  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                    Save a resource before adding media assets.
                  </div>
                ) : (
                  <>
                    {mediaItems.length > 0 ? (
                      <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                        {mediaItems.map((item) => (
                          <div key={item.id} className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
                            <div>
                              <div className="mb-1 flex flex-wrap items-center gap-2">
                                <span className="rounded bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-blue-700">{item.media_type}</span>
                                {item.is_primary && <span className="rounded bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-700">Primary</span>}
                                <span className="text-xs text-slate-400">{item.publication_status}</span>
                              </div>
                              <p className="font-semibold text-slate-900">{item.title}</p>
                              <p className="mt-1 break-all text-xs text-slate-500">{item.embed_url || item.media_url || item.caption_url || 'Stored text asset'}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeMedia(item)}
                              disabled={mediaSaving}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-red-100 bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-40"
                              title="Remove media"
                            >
                              <SafeIcon icon={FiIcons.FiTrash2} className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                        No media assets attached yet.
                      </div>
                    )}

                    <form onSubmit={saveMedia} className="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-[160px_160px_1fr]">
                        <label className="text-sm font-medium text-slate-700">
                          Media Type
                          <select
                            value={mediaForm.media_type}
                            onChange={(event) => setMediaForm((current) => ({ ...current, media_type: event.target.value }))}
                            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500"
                          >
                            {mediaTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                          </select>
                        </label>
                        <label className="text-sm font-medium text-slate-700">
                          Source
                          <select
                            value={mediaForm.source_type}
                            onChange={(event) => setMediaForm((current) => ({ ...current, source_type: event.target.value }))}
                            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500"
                          >
                            <option value="external_url">external_url</option>
                            <option value="embed">embed</option>
                            <option value="supabase_storage">supabase_storage</option>
                            <option value="text">text</option>
                          </select>
                        </label>
                        <label className="text-sm font-medium text-slate-700">
                          Title
                          <input
                            type="text"
                            value={mediaForm.title}
                            onChange={(event) => setMediaForm((current) => ({ ...current, title: event.target.value }))}
                            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                            required
                          />
                        </label>
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <label className="text-sm font-medium text-slate-700">
                          Media URL
                          <input
                            type="url"
                            value={mediaForm.media_url}
                            onChange={(event) => setMediaForm((current) => ({ ...current, media_url: event.target.value }))}
                            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                          />
                        </label>
                        <label className="text-sm font-medium text-slate-700">
                          Embed URL
                          <input
                            type="url"
                            value={mediaForm.embed_url}
                            onChange={(event) => setMediaForm((current) => ({ ...current, embed_url: event.target.value }))}
                            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                          />
                        </label>
                        <label className="text-sm font-medium text-slate-700">
                          Thumbnail URL
                          <input
                            type="url"
                            value={mediaForm.thumbnail_url}
                            onChange={(event) => setMediaForm((current) => ({ ...current, thumbnail_url: event.target.value }))}
                            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                          />
                        </label>
                        <label className="text-sm font-medium text-slate-700">
                          Caption URL
                          <input
                            type="url"
                            value={mediaForm.caption_url}
                            onChange={(event) => setMediaForm((current) => ({ ...current, caption_url: event.target.value }))}
                            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                          />
                        </label>
                      </div>

                      <label className="block text-sm font-medium text-slate-700">
                        Description
                        <textarea
                          rows={2}
                          value={mediaForm.description}
                          onChange={(event) => setMediaForm((current) => ({ ...current, description: event.target.value }))}
                          className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                        />
                      </label>

                      <label className="block text-sm font-medium text-slate-700">
                        Transcript Text
                        <textarea
                          rows={4}
                          value={mediaForm.transcript_text}
                          onChange={(event) => setMediaForm((current) => ({ ...current, transcript_text: event.target.value }))}
                          className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                        />
                      </label>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_130px_130px_130px]">
                        <label className="text-sm font-medium text-slate-700">
                          Alt Text
                          <input
                            type="text"
                            value={mediaForm.alt_text}
                            onChange={(event) => setMediaForm((current) => ({ ...current, alt_text: event.target.value }))}
                            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                          />
                        </label>
                        <label className="text-sm font-medium text-slate-700">
                          Duration Sec.
                          <input
                            type="number"
                            value={mediaForm.duration_seconds}
                            onChange={(event) => setMediaForm((current) => ({ ...current, duration_seconds: event.target.value }))}
                            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                          />
                        </label>
                        <label className="text-sm font-medium text-slate-700">
                          Priority
                          <input
                            type="number"
                            value={mediaForm.display_priority}
                            onChange={(event) => setMediaForm((current) => ({ ...current, display_priority: event.target.value }))}
                            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                          />
                        </label>
                        <label className="text-sm font-medium text-slate-700">
                          Status
                          <select
                            value={mediaForm.publication_status}
                            onChange={(event) => setMediaForm((current) => ({ ...current, publication_status: event.target.value }))}
                            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500"
                          >
                            {statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                          </select>
                        </label>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-wrap gap-4">
                          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                            <input
                              type="checkbox"
                              checked={mediaForm.is_primary}
                              onChange={(event) => setMediaForm((current) => ({ ...current, is_primary: event.target.checked }))}
                              className="h-4 w-4 rounded border-slate-300"
                            />
                            Primary
                          </label>
                          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                            <input
                              type="checkbox"
                              checked={mediaForm.is_downloadable}
                              onChange={(event) => setMediaForm((current) => ({ ...current, is_downloadable: event.target.checked }))}
                              className="h-4 w-4 rounded border-slate-300"
                            />
                            Downloadable
                          </label>
                        </div>
                        <button
                          type="submit"
                          disabled={mediaSaving}
                          className="inline-flex items-center justify-center rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50"
                        >
                          <SafeIcon icon={mediaSaving ? FiIcons.FiLoader : FiIcons.FiPlus} className={`mr-2 h-4 w-4 ${mediaSaving ? 'animate-spin' : ''}`} />
                          Add Media
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningContentAdminPage;
